import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import type { Future } from '@warp-drive/core/request';
import type { TypedRecordInstance } from '@warp-drive/core/types/record';
import type { WithLegacy } from '@warp-drive/legacy/model/migration-support';
import { modifier } from 'ember-modifier';

import { deepHasChanged } from '../data/deep-has-changed.ts';
import { deepRollback } from '../data/deep-rollback.ts';
import { deepSave, DeepSaveError } from '../data/deep-save.ts';
import { type EditingContext, EditingStoppedReason } from './edit-context.gts';
import { Validation, ValidationMessage } from './validation.gts';

export function initialState(item: { id: string | null }): 'readonly' | 'edit' {
  if (item.id === null) {
    return 'edit';
  } else {
    return 'readonly';
  }
}

export interface EditRecordSignature<T, A extends object = object> {
  Args: {
    /**
     * The item to edit.
     */
    item: T;
    /**
     * The editing status.
     */
    context: EditingContext;
  } & A;
  Element: HTMLFormElement;
}

interface MySignature<T> extends EditRecordSignature<T> {
  Args: {
    /**
     * The item to edit.
     */
    item: T;
    /**
     * The editing status.
     */
    context: EditingContext;
    /**
     * The label to display for the edit button.
     */
    editLabel: string;
    /**
     * The callback to call when the edit button is clicked. Optional.
     */
    onEdit?: () => void;
    /**
     * The callback to call when the form needs to be validated before saving.
     */
    validate?: (item: T) => Validation;
    /**
     * The callback to call when the save button is clicked.
     * If not provided, the changes will be saved using the `deepSave` utility.
     */
    save?: () => Future<unknown> | Promise<unknown>;
    /**
     * The callback to call when the revert button is clicked.
     * If not provided, the changes will be reverted using the `deepRollback` utility.
     */
    revert?: () => void;
    /**
     * The size of the buttons.
     */
    buttonSize?: 'btn-sm' | 'btn-md' | 'btn-lg';
    /**
     * The initial state of the form. Defaults to 'readonly'.
     */
    initialState?: 'readonly' | 'edit';
  };
  Blocks: {
    /**
     * The block to render the form.
     */
    form: [
      uniqueId: string,
      disabled: boolean,
      readonly?: boolean,
      validation?: Validation,
    ];
    /**
     * The block to render the buttons. Optional.
     */
    buttons?: [uniqueId: string, readonly: boolean, validation?: Validation];
  };
}

abstract class EditRecordPrivate<
  T extends WithLegacy<TypedRecordInstance>,
> extends Component<MySignature<T>> {
  @tracked private _readonly = this.args.initialState !== 'edit';

  get readonly() {
    return this._readonly;
  }

  /**
   * Starts the editing process.
   */
  startEditing() {
    if (this.args.context.isEditing) {
      console.log('Editing already started');
      return;
    }

    this._readonly = false;
    this.args.context.editingStarted();
  }

  /**
   * Stops the editing process.
   */
  stopEditing(reason: EditingStoppedReason) {
    this._readonly = true;
    this.args.context.editingStopped(reason);
  }
}

export class EditRecord<
  T extends WithLegacy<TypedRecordInstance>,
> extends EditRecordPrivate<T> {
  @tracked saving = false;
  @tracked validation: Validation = Validation.success();
  @service declare store: Store;
  @service declare router: RouterService;

  /**
   * The function to call when the form is submitted.
   * Saves the changes to the record then sets the form to readonly.
   */
  submitClicked = (event: Event) => {
    event.preventDefault();

    this.validation =
      this.args.validate?.(this.args.item) ?? Validation.success();
    if (this.validation.isFailure) {
      return;
    }

    this.saving = true;

    void this.save()
      .then(() => {
        this.stopEditing(EditingStoppedReason.Saved);
        this.validation = Validation.success();
      })
      .catch((error: unknown) => {
        console.error('Save failed: ', error);
        if (!(error instanceof DeepSaveError)) {
          this.stopEditing(EditingStoppedReason.SaveFailed);
        }
        this.validation = Validation.fromError(error as Error);
      })
      .finally(() => {
        this.saving = false;
      });
  };

  /**
   * The function to call when the cancel button is clicked.
   * Reverts the changes and sets the form to readonly.
   */
  cancelClicked = (event: Event) => {
    event.preventDefault();
    this.revert();
    this.stopEditing(EditingStoppedReason.Cancelled);
    this.validation = Validation.success();
  };

  /**
   * The function to call when the edit button is clicked.
   * Sets the form to editable.
   */
  editClicked = (event: Event) => {
    event.preventDefault();
    this.startEditing();
    this.args.onEdit?.();
  };

  /**
   * Calls the `save` callback if provided, otherwise uses the `deepSave` utility.
   */
  save(): Future<unknown> | Promise<unknown> {
    if (this.args.save) {
      return this.args.save();
    } else {
      return deepSave(this.args.item, this.store);
    }
  }

  /**
   * Calls the `revert` callback if provided, otherwise uses the `deepRollback` utility.
   */
  revert(): void {
    if (this.args.revert) {
      this.args.revert();
    } else {
      deepRollback(this.args.item, this.store);
    }
  }

  /**
   * Returns true if the form is disabled or saving.
   */
  get isDisabled() {
    return this.saving || this.readonly;
  }

  get buttonSize() {
    return this.args.buttonSize || 'btn-sm';
  }

  /**
   * Intercepts the transition and prompts the user to confirm if there are unsaved changes.
   */
  interceptTransition = modifier(() => {
    const routeWillChange = (transition: Transition) => {
      if (transition.isAborted) {
        console.log('transition is aborted');
        return;
      }

      console.log('routeWillChange', transition);
      if (deepHasChanged(this.args.item, this.store)) {
        const confirmed = confirm(
          'You have unsaved changes. Are you sure you want to leave this page?'
        );
        if (confirmed) {
          console.log('reverting changes and continuing transition');
          this.revert();
        } else {
          console.log('aborting transition');
          transition.abort();
        }
      }
    };

    this.router.on('routeWillChange', routeWillChange);
    return () => {
      this.router.off('routeWillChange', routeWillChange);
    };
  });

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <form {{on "submit" this.submitClicked}} ...attributes>
        {{yield
          uniqueId
          this.isDisabled
          this.readonly
          this.validation
          to="form"
        }}

        <fieldset disabled={{this.saving}}>
          {{#if this.readonly}}
            <ValidationMessage @validation={{this.validation}} />

            <div class="text-center">
              <button
                type="button"
                class="btn btn-primary {{this.buttonSize}}"
                {{on "click" this.editClicked}}
                disabled={{@context.isEditing}}
              >{{@editLabel}}</button>
            </div>
          {{else}}
            <ValidationMessage @validation={{this.validation}} />

            {{#if (has-block "buttons")}}
              <div
                class="d-flex justify-content-between gap-2"
                {{this.interceptTransition}}
              >
                <div class="d-flex justify-content-center gap-2">
                  {{yield uniqueId this.readonly this.validation to="buttons"}}
                </div>
                <div class="d-flex justify-content-center gap-2">
                  <button
                    type="submit"
                    class="btn btn-primary {{this.buttonSize}}"
                  >Save</button>
                  <button
                    type="button"
                    class="btn btn-outline-primary {{this.buttonSize}}"
                    {{on "click" this.cancelClicked}}
                  >Cancel</button>
                </div>
              </div>
            {{else}}
              <div
                class="d-flex justify-content-center gap-2"
                {{this.interceptTransition}}
              >
                {{yield uniqueId this.readonly this.validation to="buttons"}}
                <button
                  type="submit"
                  class="btn btn-primary {{this.buttonSize}}"
                >Save</button>
                <button
                  type="button"
                  class="btn btn-outline-primary {{this.buttonSize}}"
                  {{on "click" this.cancelClicked}}
                >Cancel</button>
              </div>
            {{/if}}
          {{/if}}
        </fieldset>
      </form>
    {{/let}}
  </template>
}
